import { Col, Container, Row } from 'react-bootstrap';
import { DELIVERY_TYPES, PLATFORMS } from '../../../../utils/constants';

import Accordion from '../../../../shared/components/Accordion';
import ContentRightsCard from '../components/ContentRightsCard';
import { Link } from 'react-router-dom';
import PaymentCard from '../components/PaymentCard';
import React from 'react';
import useScrollToHash from '../../../../shared/hooks/useScrollToHash';
import { useTranslation } from 'react-i18next';
import userSession from '../../../../utils/userSession';
import { get } from 'lodash-es';

function Brief({ creativeBrief, offer }) {
  const { t } = useTranslation();
  const userId = userSession('id');
  const lumanuEmail = userSession('lumanu_email');

  useScrollToHash(120, 280);

  if (!creativeBrief) return null;

  const renderDeliverablesSection = () => {
    if (!creativeBrief.deliverables_include) return null;

    let collapsibles = [{
      header: t('influencers.pages.campaign.brief.deliverables'),
      content: <React.Fragment>
        <div
          dangerouslySetInnerHTML={{
            __html: creativeBrief.deliverables
          }}></div>
      </React.Fragment>
    }];

    PLATFORMS.forEach(platform => {
      DELIVERY_TYPES.forEach(deliveryType => {
        const item = `${platform}_${deliveryType}`;
        const includes = creativeBrief[`${item}_include`];

        let deliverableNumRequired = offer.deliverables[platform];
        if (deliveryType == 'stories')
          deliverableNumRequired = offer.deliverables[`${platform}_stories`];
        let deliverableTitle = t(`influencers.pages.campaign.brief.${item}`, { num: deliverableNumRequired });
        if (deliveryType == 'reels_only')
          deliverableTitle = t(`influencers.pages.campaign.brief.${item}`);
        if (deliveryType == 'other')
          deliverableTitle = `${deliverableNumRequired}x ${get(offer, 'other_deliverable_label') || deliverableTitle}`;

        if (includes === 'on' && (deliverableNumRequired > 0 || deliveryType == 'reels_only')) {
          collapsibles.push({
            header: deliverableTitle,
            content: <div
              dangerouslySetInnerHTML={{
                __html: creativeBrief[item]
              }}></div>
          });
        }
      });
    });

    if (creativeBrief.tiktok_one_include === 'on') {
      collapsibles.push({
        header: t('influencers.pages.campaign.brief.tiktok_one'),
        content: <div
          dangerouslySetInnerHTML={{
            __html: creativeBrief.tiktok_one
          }}></div>
      });
    }

    return (<Row className='mb-6'>
      <Col xs='12'>
        <h4 className='mb-4 scroll-margin-top-120' id="deliverables">
          {t('influencers.pages.campaign.brief.deliverables')}
        </h4>
      </Col>
      <Col xs='12'>
        <div className='mb-3 mt-3'>
          {t('influencers.pages.campaign.brief.content_must')}&nbsp;
          <Link to="/terms-of-service">
            {t('influencers.pages.campaign.brief.guidelines')}
          </Link>
        </div>
        <div>
          {offer.inst_user_post_stats_required ?
            t('influencers.pages.campaign.brief.inst_user_post_stats_required')
            : null
          }
        </div>
        <div>
          {offer.link_user_post_stats_required ?
            t('influencers.pages.campaign.brief.link_user_post_stats_required')
            : null
          }
        </div>
        <Accordion className='mb-3 mt-3'
          collapsibles={collapsibles} />
      </Col>
    </Row>
    );
  };

  const renderAccordion = (title, items) => {
    const collapsibles = items.filter(item => {
      const includes = creativeBrief[`${item}_include`];
      return includes === 'on' || includes === true;
    }).map(item => ({
      header: t(`influencers.pages.campaign.brief.${item}`),
      content: <div
        dangerouslySetInnerHTML={{
          __html: creativeBrief[item]
        }}></div>
    }));

    if (!collapsibles.length) return null;

    return (
      <Row className='mb-6'>
        <Col xs='12'>
          <h4 className='mb-4'>
            {title}
          </h4>
        </Col>
        <Col xs='12'>
          <Accordion
            collapsibles={collapsibles} />
        </Col>
      </Row>
    );
  };

  const renderLinksTagsSection = () => renderAccordion(
    t('influencers.pages.campaign.brief.links_tags'),
    ['hashtags_and_mentions', 'links', 'promo_codes']
  );

  const renderContentRequirementsSection = () => renderAccordion(
    t('influencers.pages.campaign.brief.content_requirements'),
    ['imagery', 'copy_and_writing', 'brand_sensitivities']
  );

  return (
    <div className='page'>
      <Container>
        <Row>
          <Col>
            <h3 className="mb-5">{t('influencers.pages.campaign.brief.title')}</h3>
          </Col>
        </Row>
        <Row>
          <Col sm='3' className="mb-6">
            <Row>
              <Col xs={12} className="mb-3">
                <h4>{t('influencers.pages.campaign.brief.goal')}</h4>
              </Col>
              {
                offer.description &&
                <Col xs={12} className="mb-3">
                  <div className="body-1">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: offer.description
                      }}>
                    </div>
                  </div>
                </Col>
              }
              {
                (offer.payments && (offer.payments.length > 0)) &&
                <Col xs={12} className="mb-2">
                  <PaymentCard
                    isLogged={!!userId}
                    payments={offer.payments}
                    extraPayments={offer.extra_payments}
                    incentives={offer.incentives}
                    lumanuEnabled={offer.lumanu_enabled}
                    lumanuEmail={lumanuEmail}
                  />
                </Col>
              }
              {
                (!offer.hide_media_rights_price && offer.media_rights) || offer.show_content_rights_expiration_instructions &&
                <Col xs={12}>
                  <ContentRightsCard
                    isLogged={!!userId}
                    contentRights={offer.media_rights}
                    contentRightsExpirationInstructions={offer.show_content_rights_expiration_instructions &&
                        offer.content_rights_expiration_instructions}
                  />
                </Col>
              }
            </Row>
          </Col>

          <Col sm={{
            offset: 1,
            span: 8
          }} >
            {renderDeliverablesSection()}
            {renderLinksTagsSection()}
            {renderContentRequirementsSection()}
            {
              (creativeBrief.music_rights_include || creativeBrief.paid_amplification_include) &&
              <Row className='mb-6'>
                <Col xs='12'>
                  <h4 className='mb-4'>
                    {t('influencers.pages.campaign.brief.other_requirements')}
                  </h4>
                </Col>
                <Col xs='12'>
                  {
                    creativeBrief.music_rights_include &&
                    <div>
                      <Link to="./questions#music">
                        {t('influencers.pages.campaign.brief.music_rights')}
                      </Link>
                    </div>
                  }
                  {
                    creativeBrief.paid_amplification_include &&
                    <div>
                      <Link to="./questions#paid_amplification">
                        {t('influencers.pages.campaign.brief.paid_amplification')}
                      </Link>
                    </div>
                  }
                </Col>
              </Row>
            }
          </Col>
        </Row>
      </Container>
    </div>);
}

export default Brief;
